import { isAuthenticatedUser } from 'js/lib/user';

import useBuildOnboardingUrl from 'bundles/onboarding-common/hooks/useBuildOnboardingUrl';
import {
  ACCOUNT_SETTINGS_TAB_QUERY_PARAM,
  PERSONALIZATION_TOP_SECTION,
} from 'bundles/personalization-settings/constants';
import { useIsEditGoalRedirectEnabled } from 'bundles/personalization-settings/featureFlags';

const PERSONALIZATION_SETTINGS_URL = `/account-settings?${ACCOUNT_SETTINGS_TAB_QUERY_PARAM}=${PERSONALIZATION_TOP_SECTION}`;

type UseEditGoalUrlArgs = {
  hasCompletedOnboardingLoading?: boolean;
  hasCompletedOnboarding?: boolean;
} & Parameters<typeof useBuildOnboardingUrl>[0];

/**
 * Destination for the site-wide "Edit goal" / edit-profile entry points.
 *
 * Historically these CTAs deep-link into the onboarding stepper (via
 * {@link useBuildOnboardingUrl}). When the edit-goal-redirect-layer routes here,
 * we instead route the learner to the Personalization tab on Account Settings,
 * where they can edit the same Learner Object attributes (goal, role, skills,
 * education) without re-running onboarding (LO-4742). Redirecting also
 * requires the learner to be authenticated, since the Personalization tab
 * lives behind Account Settings auth and unauthenticated learners must go
 * through onboarding regardless of the redirect layer's value — so the layer
 * read (and its impression) is skipped entirely when unauthenticated.
 *
 * Takes the same arguments as {@link useBuildOnboardingUrl} and returns the same
 * `{ url, loading }` shape, so call sites swap the hook in place.
 */
const useEditGoalUrl = (args: UseEditGoalUrlArgs): { url: string; loading: boolean } => {
  const { hasCompletedOnboarding, hasCompletedOnboardingLoading, ...buildOnboardingUrlArgs } = args;
  const shouldCheckHasCompletedOnboarding =
    hasCompletedOnboardingLoading !== undefined || hasCompletedOnboarding !== undefined;

  // Skip the impression call when not authenticated since we can't redirect to account-settings
  // Also skip when not yet completed onboarding
  const isAuthenticated = isAuthenticatedUser();
  const shouldSkip =
    !isAuthenticated ||
    (shouldCheckHasCompletedOnboarding && hasCompletedOnboardingLoading === false && hasCompletedOnboarding === false);
  const isEditGoalRedirectEnabled = useIsEditGoalRedirectEnabled({ skip: shouldSkip });

  // Always call the onboarding builder so hook order is stable and we keep the
  // exact gate-off behavior; only the destination changes when the gate is on.
  const { onboardingUrl, loading } = useBuildOnboardingUrl(buildOnboardingUrlArgs);

  if (hasCompletedOnboardingLoading) {
    return { url: onboardingUrl, loading: hasCompletedOnboardingLoading && loading };
  }

  if (
    isEditGoalRedirectEnabled &&
    isAuthenticated &&
    (!shouldCheckHasCompletedOnboarding || (shouldCheckHasCompletedOnboarding && hasCompletedOnboarding))
  ) {
    return { url: PERSONALIZATION_SETTINGS_URL, loading: false };
  }

  return { url: onboardingUrl, loading };
};

export default useEditGoalUrl;
